import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { functions } from '../full'

import type { Feature } from './feature'
import * as gql from './gql'
import type { TypeParams } from './types'

/**
 * API service for space organizations. Provides methods to list organizations
 * with filtering, update an organization's joint use information, and retrieve
 * attributes for organizations.
 */
export class Api implements ec.models.Api<TypeParams> {
  /** @inheritdoc */
  public list = ec.models.Api.list.using.onlyFilter<TypeParams>(
    (context, { filter }) => {
      if (!context) {
        return rxjs.of([])
      }

      return gql.queries.organizations.executor({
        ...context,
        entityId: this._config.entityId,
        filter: gql.inputs.filter.TRANSFORMER.getGqlFrom({
          ...filter?.additionalFilters,
          includeInaccessible: this._config.includeInaccessible,
        }),
      })
    },
  )

  constructor(private readonly _config: Api.Config) {}

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.updateOrganizationJointUse.executor({
      ...context,
      organizationEntityId: TypeAsserter.validateButJustWarn(
        updates.entityId ?? this._config.entityId,
        TypeAsserter.assertIsNotNullish,
      ),
      organizationId: id,
      update: gql.inputs.jointUseUpdate.TRANSFORMER.getGqlFrom(
        TypeAsserter.validateButJustWarn(
          updates.jointUseInfo,
          TypeAsserter.assertIsNotNullish,
        ),
      ),
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs
      .combineLatest([
        rxjs.of([
          ec.models.Attribute.fromKey<TypeParams>('code', {
            isSortable: true,
            name: 'Code',
          }),
          ec.models.Attribute.fromKey<TypeParams>('name', {
            isSortable: true,
            name: 'Name',
          }),
        ]),
        this._getParentAttribute$(),
        this._getJointUseCalculationAttribute$(),
      ])
      .pipe(
        rxjs.map(([baseAttributes, ...additionalAttributes]) => [
          ...baseAttributes,
          ...additionalAttributes,
        ]),
      )

  /**
   * Retrieves the "Parent" attribute for space organizations, which displays
   * the name of the parent organization based on the `parent` field.
   */
  private _getParentAttribute$(): rxjs.Observable<
    ec.models.Attribute<TypeParams>
  > {
    return this._config.organizationsFeature.selectors
      .getParentEc(this._config.entityId)
      .asObservable.pipe(
        rxjs.switchMap(
          parentEc =>
            parentEc?.plugins.cachePlugin.feature.selectors.lookup
              .asObservable ?? rxjs.of(undefined),
        ),
        rxjs.map(lookup => ({
          getValueFrom: ({ parentId }) => parentId && lookup?.[parentId]?.name,
          id: 'parent',
          isSortable: true,
          name: 'Parent',
        })),
      )
  }

  /**
   * Displays the joint use calculation mode for the organization.
   *
   * If the calculation mode is manual with functionalizations, it shows each
   * function's code and percentage. If the calculation mode is manual without
   * functionalizations, it shows "Manual". If the calculation mode is not
   * manual, it shows the mode name.
   */
  private _getJointUseCalculationAttribute$(): rxjs.Observable<
    ec.models.Attribute<TypeParams>
  > {
    return ec.Collection.instances
      .get<functions.TypeParams>(ec.Name.Predefined.SpaceFunction)
      .asObservable.pipe(
        rxjs.switchMap(functionsEc =>
          functionsEc.plugins.cachePlugin.feature.selectors.lookup.asObservable.pipe(
            rxjs.map(
              lookup =>
                ({
                  getValueFrom: ({ jointUseInfo }) => {
                    // If no joint use info, show nothing
                    if (!jointUseInfo) {
                      return undefined
                    }
                    const { calculationMode, manualFunctionalizations } =
                      jointUseInfo

                    // If not manual mode, show only the mode name
                    if (
                      !ents.extra.space.Organization.JointUseCalculationMode.isManual(
                        calculationMode,
                      )
                    ) {
                      return calculationMode
                    }

                    // If manual mode but no functionalizations, show the mode name
                    // (indicates manual mode with no functions assigned)
                    if (
                      !manualFunctionalizations ||
                      manualFunctionalizations.length === 0
                    ) {
                      return ents.extra.space.Organization
                        .JointUseCalculationMode.Manual
                    }

                    // If manual mode with functionalizations, show each function's code
                    // and percentage
                    return manualFunctionalizations
                      .map(({ functionId, percent }) => {
                        const fn = lookup[functionId]
                        return `${fn?.code ?? functionId}: ${Math.round(percent * 100)}%`
                      })
                      .join('; ')
                  },
                  id: 'jointUseCalculation',
                  name: 'Joint Use Calculation',
                }) satisfies ec.models.Attribute<TypeParams>,
            ),
          ),
        ),
      )
  }
}

export namespace Api {
  export interface Config {
    entityId: ents.core.dms.models.Entity.Id
    organizationsFeature: Feature
    includeInaccessible?: true
  }
}
